import { createAppRequest } from '@/services/request';
import type { MesOperator } from '@/mes-demo/store';

const request = createAppRequest();
const prefix = '/api/mes/v1';

const idempotencyKey = (action: string) =>
  `mes-web:${action}:${Date.now()}:${Math.random().toString(36).slice(2, 8)}`;

type MesRequestOptions = {
  method?: 'GET' | 'POST';
  params?: Record<string, string | undefined>;
  data?: Record<string, unknown>;
};

export type PreparedProductionMaterial = {
  materialCode: string;
  lot: string;
  availableQuantity: number;
  locationCode: string;
};

async function call<T>(path: string, options: MesRequestOptions = {}) {
  return request<T>(`${prefix}${path}`, {
    method: options.method || 'GET',
    params: options.params,
    data: options.data,
    headers: options.method === 'POST'
      ? { 'Idempotency-Key': idempotencyKey(path), 'Content-Type': 'application/json' }
      : undefined,
  });
}

export const mesApi = {
  listOrders: () => call<unknown[]>('/execution-orders'),
  listPreparedMaterials: (uuid: string) => call<{ wmsOutboundOrderUuid: string; stagingLocationCode: string; materials: PreparedProductionMaterial[] }>(`/execution-orders/${uuid}/prepared-materials`),
  listTasks: () => call<unknown[]>('/operation-tasks'),
  listOperators: () => call<MesOperator[]>('/operators'),
  listReports: () => call<unknown[]>('/reports'),
  listExceptions: () => call<unknown[]>('/production-exceptions'),
  listReworks: () => call<unknown[]>('/rework-tasks'),
  listIntegrations: () => call<unknown[]>('/integration-messages'),
  trace: (serialNo: string) => call<unknown[]>(`/traceability/${encodeURIComponent(serialNo.trim())}`),
  completeOrder: (uuid: string) => call(`/execution-orders/${uuid}/complete`, { method: 'POST' }),
  reconcileCompletionMaterial: (uuid: string, data: Record<string, unknown>) => call(`/execution-orders/${uuid}/material-reconciliation`, { method: 'POST', data }),
  dispatchTask: (uuid: string, memberUuid: string) => call(`/operation-tasks/${uuid}/dispatch`, {
    method: 'POST', data: { memberUuid },
  }),
  claimTask: (uuid: string) => call(`/operation-tasks/${uuid}/claim`, { method: 'POST' }),
  startTask: (uuid: string) => call(`/operation-tasks/${uuid}/start`, { method: 'POST' }),
  submitReport: (uuid: string, data: Record<string, unknown>) => call(`/operation-tasks/${uuid}/reports`, { method: 'POST', data }),
  returnMaterial: (uuid: string, data: Record<string, unknown>) => call(`/operation-tasks/${uuid}/material-returns`, { method: 'POST', data }),
  requestCorrection: (uuid: string) => call(`/reports/${uuid}/correction-request`, { method: 'POST' }),
  correctReport: (uuid: string, data: Record<string, unknown>) => call(`/reports/${uuid}/correct`, { method: 'POST', data }),
  createException: (data: Record<string, unknown>) => call('/production-exceptions', { method: 'POST', data }),
  completeRework: (uuid: string) => call(`/rework-tasks/${uuid}/complete`, { method: 'POST' }),
  retryIntegration: (uuid: string) => call(`/integration-messages/${uuid}/retry`, { method: 'POST' }),
};
